import { ICampaignMessage, ICampaignRequest } from '@dataTypes/marketing.type'
import axios from '../axiosConfig'
import { getEnv } from '@utils/env'
import { logger } from '@utils/safeLogger'
import { toApiError } from '../errors/api-error'

// Constants
const BUSINESS_LAYER_API = getEnv('REACT_APP_BUSINESS_LAYER_MARKETING', '')
const EXTERNAL_LAYER_API = getEnv('REACT_APP_EXTERNAL_LAYER_MARKETING', '')

const headers = {
  'Content-Type': 'application/json',
}

const marketingHeaders = (): Record<string, string> => {
  const dbName = localStorage.getItem('db_name')
  return dbName ? { ...headers, 'x-db-name': dbName } : headers
}

// Types
interface ApiResponse<T> {
  statusCode: number
  message: string
  data: T
}

type CampaignsResponseData = {
  campaigns?: ICampaignRequest[]
}

type CampaignResponseData = {
  campaign?: ICampaignRequest
}

type CampaignMessagesResponseData = {
  campaign_messages?: ICampaignMessage[]
}

type SendSmsResponseData = {
  campaign_message?: Partial<ICampaignMessage>
}

type GenericResponseData = Record<string, unknown>

type GetAllCampaignResponse = ApiResponse<CampaignsResponseData>
type UpdateCampaignResponse = ApiResponse<CampaignsResponseData>
type CreateCampaignResponse = ApiResponse<CampaignResponseData>
type SendCampaignSmsResponse = ApiResponse<CampaignMessagesResponseData>
type SendSmsResponse = ApiResponse<SendSmsResponseData>

// API Functions
export const getAllCampaigns = async (): Promise<GetAllCampaignResponse> => {
  try {
    const response = await axios.get<GetAllCampaignResponse>(
      `${BUSINESS_LAYER_API}/api/v1/campaign/all`,
      {
        headers: marketingHeaders(),
      },
    )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in getAllCampaigns:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const updateCampaignById = async (
  campaign_id: string,
  updatedData: Partial<ICampaignRequest>,
): Promise<UpdateCampaignResponse> => {
  try {
    const response = await axios.put<UpdateCampaignResponse>(
      `${BUSINESS_LAYER_API}/api/v1/campaign`,
      { campaign_id, data: updatedData },
      {
        headers: marketingHeaders(),
      },
    )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in updateCampaignById:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const createCampaign = async (
  campaignDetails: ICampaignRequest,
): Promise<CreateCampaignResponse> => {
  try {
    const response = await axios.post<CreateCampaignResponse>(
      `${BUSINESS_LAYER_API}/api/v1/campaign`,
      campaignDetails,
      {
        headers: marketingHeaders(),
      },
    )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in createCampaign:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const deleteCampaignById = async (
  campaignId: string,
): Promise<CreateCampaignResponse> => {
  try {
    const response = await axios.delete<CreateCampaignResponse>(
      `${BUSINESS_LAYER_API}/api/v1/campaign/${campaignId}`,
      {
        headers: marketingHeaders(),
      },
    )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in deleteCampaignById:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const sendBulkSms = async (
  sid: string,
  token: string,
  messages: ICampaignMessage[],
): Promise<SendCampaignSmsResponse> => {
  try {
    const response = await axios.post<SendCampaignSmsResponse>(
      `${EXTERNAL_LAYER_API}/api/v1/external/campaign/immediate-bulk-sms`,
      { sid, token, data: messages },
      {
        headers: marketingHeaders(),
      },
    )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in sendCampaignSms:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const sendScheduledBulkSms = async (
  sid: string,
  token: string,
  messages: ICampaignMessage[],
): Promise<SendCampaignSmsResponse> => {
  try {
    const response = await axios.post<SendCampaignSmsResponse>(
      `${EXTERNAL_LAYER_API}/api/v1/external/campaign/scheduled-bulk-sms`,
      { sid, token, data: messages },
      {
        headers: marketingHeaders(),
      },
    )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in sendCampaignSms:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const sendBulkEmail = async (
  messages: ICampaignMessage[],
  clinicId?: string,
): Promise<SendCampaignSmsResponse> => {
  try {
    const response = await axios.post<SendCampaignSmsResponse>(
      `${BUSINESS_LAYER_API}/api/v1/campaign/send/immediate-bulk-email`,
      {
        campaign_messages: messages,
        ...(clinicId ? { clinic_id: clinicId } : {}),
      },
      {
        headers: marketingHeaders(),
      },
    )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in sendBulkEmail:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const sendScheduledBulkEmail = async (
  messages: ICampaignMessage[],
  clinicId?: string,
): Promise<SendCampaignSmsResponse> => {
  try {
    const response = await axios.post<SendCampaignSmsResponse>(
      `${BUSINESS_LAYER_API}/api/v1/campaign/send/scheduled-bulk-email`,
      {
        campaign_messages: messages,
        ...(clinicId ? { clinic_id: clinicId } : {}),
      },
      {
        headers: marketingHeaders(),
      },
    )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in sendScheduledBulkEmail:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const sendSms = async (
  sid: string,
  token: string,
  message: ICampaignMessage,
): Promise<SendSmsResponse> => {
  try {
    const response = await axios.post<SendSmsResponse>(
      `${EXTERNAL_LAYER_API}/api/v1/external/campaign/send-sms`,
      { sid, token, data: message },
      {
        headers: marketingHeaders(),
      },
    )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in sendSms:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}
